import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom';
import { CurrentUserProvider } from '@uplift/providers';

import {
  EMPTY_STRING,
  PROVIDER_ONBOARDING_ROUTES,
  ROUTING_CONFIG,
} from '@uplift/constants';
import { useStore, useDispatchUserId } from '@uplift/hooks';
import {
  sentryInit,
  TimezoneProvider,
  useCurrentuser,
  useCurrentuserRefetch,
} from '@uplift/contexts';

import {
  LocalStorageService,
} from '@uplift/services';
import { PrivateRoute, Spinner, AlertProvider, AutoLogoutProvider, MainLayoutProvider, UpliftThemeProvider, MessagingProvider, ProviderMessagingModal } from '@uplift/ui-v2';
import { environment } from '../environments/environment';

import {
  AppointmentSessionPage,
  DashboardPage,
  FinancePage,
  FinanceMonthPage,
  PatientDetailsPage,
  PatientsPage,
  ProfilePage,
  SchedulePage,
  SettingsPage,
  SupportPage,
  PatientIntakeFormPage,
  UnsubmittedSessionsPage,
  SubmitSessionPage,
} from './pages';
import { Page404 } from './pages/page404/page404';
import { NotesPage } from './pages/notes-page';
import { OnboardingPage } from './pages/onboarding-page';
import { PracticeStatesPage } from './pages/practice-states-page';


if (environment.production) {
  sentryInit(environment.sentryDSN);
}

const Router = observer(() => {
  const me = useCurrentuser();
  const { pathname } = useLocation();
  const { authStore } = useStore();
  const { refetchUser } = useCurrentuserRefetch();

  const isRegular = me?.status ? me?.status === 'regular' : null;
  const isInitial = me?.status === 'initial';

  useEffect(() => {
    authStore.tryRefresh(window.location, EMPTY_STRING, refetchUser);
  }, []);

  const isOnboarding = PROVIDER_ONBOARDING_ROUTES.some((route) =>
    pathname.includes(route)
  );

  const loading = authStore.loading || (authStore.isLoggedIn && isRegular == null);
  const showLoader = !isOnboarding && loading;

  useDispatchUserId();

  if (showLoader) {
    return (
      <Box
        height="100vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (isInitial && !PROVIDER_ONBOARDING_ROUTES.includes(window.location.pathname)) {
    return <Redirect to={ROUTING_CONFIG.onboardingInvite} />;
  }

  return (
    <Switch>
      <Route exact path={PROVIDER_ONBOARDING_ROUTES}>
        <OnboardingPage />
      </Route>

      <Route exact path={ROUTING_CONFIG.support}>
        <SupportPage />
      </Route>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.main}
      >
        {isRegular && <DashboardPage />}
        {!isRegular && <Page404 />}
      </PrivateRoute>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.schedule}
      >
        {isRegular && <SchedulePage />}
        {!isRegular && <Page404 />}
      </PrivateRoute>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.finance}
      >
        {isRegular && <FinancePage />}
        {!isRegular && <Page404 />}
      </PrivateRoute>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.financeMonth}
      >
        {isRegular && <FinanceMonthPage />}
        {!isRegular && <Page404 />}
      </PrivateRoute>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.patients}
      >
        {isRegular && <PatientsPage />}
        {!isRegular && <Page404 />}
      </PrivateRoute>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.patient}
      >
        {isRegular && <PatientDetailsPage />}
        {!isRegular && <Page404 />}
      </PrivateRoute>

      {isRegular && (
        <PrivateRoute
          redirectUrl={environment.authUrl}
          exact
          path={ROUTING_CONFIG.patientIntakeForm}
        >
          <PatientIntakeFormPage />
        </PrivateRoute>
      )}

      <PrivateRoute
        redirectUrl={environment.authUrl}
        path={ROUTING_CONFIG.profile}
      >
        {isRegular && <ProfilePage />}
      </PrivateRoute>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.settings}
      >
        {isRegular && <SettingsPage />}
        {!isRegular && <Page404 />}
      </PrivateRoute>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.notSubmittedSessions}
      >
        {isRegular && <UnsubmittedSessionsPage />}
        {!isRegular && <Page404 />}
      </PrivateRoute>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.appointmentsSubmitSession}
      >
        {isRegular && <SubmitSessionPage />}
        {!isRegular && <Page404 />}
      </PrivateRoute>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.appointmentsSession}
      >
        {isRegular && <AppointmentSessionPage />}
        {!isRegular && <Page404 />}
      </PrivateRoute>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.notes}
      >
        {isRegular && <NotesPage />}
        {!isRegular && <Page404 />}
      </PrivateRoute>

      <PrivateRoute
        redirectUrl={environment.authUrl}
        exact
        path={ROUTING_CONFIG.practiceStates}
      >
        <PracticeStatesPage />
      </PrivateRoute>

      <Route
        path={ROUTING_CONFIG.pageNotFound}
        render={() => <Page404 />}
      />

      <Route
        path={ROUTING_CONFIG.any}
        render={() => <Redirect to={ROUTING_CONFIG.main} />}
      />
    </Switch>
  );
});

export const App = observer(() => {
  LocalStorageService.setAppNameSpace(environment.appNameSpace);

  const { authStore } = useStore();

  useEffect(() => {
    authStore.getTokens();
    if (authStore.needToFetchAccessToken) {
      authStore.fetchAccessTokenByRefreshToken();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CurrentUserProvider isLoggedIn={authStore.isLoggedIn} accessToken={authStore?.tokens?.accessToken}>
      <TimezoneProvider>
        <UpliftThemeProvider>
          <AlertProvider>
            <BrowserRouter>
              <MainLayoutProvider>
                <AutoLogoutProvider>
                  <MessagingProvider>
                    <Router />
                    <ProviderMessagingModal />
                  </MessagingProvider>
                </AutoLogoutProvider>
              </MainLayoutProvider>
            </BrowserRouter>
          </AlertProvider>
        </UpliftThemeProvider>
      </TimezoneProvider>
    </CurrentUserProvider>
  );
});
